import {
  TAnswer,
  TAnswerBlock,
  TCompareCondition,
  TCondition,
  TCurrentStep,
  TError,
  TNonZeroCondition,
  TMaskCondition,
  TStage,
  TStep,
  TValidationCondition,
} from "@/models/questionnaire";
import axios from "@/plugins/http";
import { AxiosError } from "axios";
import { API_ROUTES } from "@/utils/API";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { SMHorizontalQuestionnaire, SMNotifications, SMTasks } from "..";
import { SMNps } from "@/store";

const DEFAULT_ERROR = "The data inserted is incorrect. Please check.";

@Module({ name: "MQuestionnaire", namespaced: true })
export default class MQuestionnaire extends VuexModule {
  isOpen = false;
  display_name = "";
  last_updated_step: TStep | string = "";
  socGroupNumberAnswerBlockAlias = "";
  regex = /[^a-zA-Z0-9!@#$%^&*)([\]\\/|'"–’?+=.:;}{`~£,_ -\s]+/;

  questionIsRequired = true;
  canGoNext = false;
  hasHistory = false;
  needsUpdate = false;

  loopsLimitMin = 0;
  loopsLimit = 0;
  loopsCount = 1;

  userflowID?: number;
  answerQuery: object = {};

  currentStep: TStep | null = null;
  stages: TStage[] = [];
  progress = 0;

  answers: TAnswer[] = [];
  answerBlocks: TAnswerBlock[] = [];

  hasCondition = false;
  conditions: TCondition[] = [];
  compareConditions: TCompareCondition[] = [];
  validationConditions: TValidationCondition[] = [];
  notZeroConditions: TNonZeroCondition[] = [];
  maskConditions: TMaskCondition[] = [];

  answerBlockEditing = false;
  hasNoneOfTheAboveAnswerBlock = false;
  isNoneOfTheAboveOptionSelected = false;

  errors: TError[] = [];

  @Mutation
  pushError(payload) {
    this.errors.push(payload);
  }

  @Mutation
  cleanErrors() {
    this.errors = [];
  }

  @Mutation
  setNeedsUpdate(value: boolean) {
    this.needsUpdate = value;
  }

  @Mutation
  setNoneOfTheAboveOptionSelected(selected: boolean) {
    this.isNoneOfTheAboveOptionSelected = selected;
  }

  @Mutation
  setAnswerBlockEditing(payload: boolean) {
    this.answerBlockEditing = payload;
  }

  @Mutation
  setLastUpdatedStep(step: TStep) {
    this.last_updated_step = step;
  }

  get lastUpdatedStep() {
    return this.last_updated_step;
  }

  @Mutation
  open() {
    this.isOpen = true;
  }

  @Mutation
  close() {
    this.isOpen = false;

    this.needsUpdate = true;
  }

  @Action
  async REmployeeHomeestionnaire(id: number) {
    await this.context.dispatch("getCurrentStep", id);

    this.context.commit("setNeedsUpdate", false);
  }

  @Mutation
  unsetCondition() {
    this.hasCondition = false;
    this.conditions = [];
    this.compareConditions = [];
    this.validationConditions = [];
    this.notZeroConditions = [];
    this.maskConditions = [];
    this.isSelectButtonDisabled = false;
  }

  @Mutation
  setConditionData(conditions: {
    conditions: TCondition[];
    compareConditions: TCompareCondition[];
    validationConditions: TValidationCondition[];
    notZeroConditions: TNonZeroCondition[];
    maskConditions: TMaskCondition[];
  }) {
    this.conditions = conditions.conditions;
    this.compareConditions = conditions.compareConditions;
    this.validationConditions = conditions.validationConditions;
    this.notZeroConditions = conditions.notZeroConditions;
    this.maskConditions = conditions.maskConditions;
  }

  isSelectButtonDisabled = false;

  @Mutation
  setConditionSelectButtonDisabled() {
    this.isSelectButtonDisabled = true;
  }

  @Action
  setCondition(answers: TAnswerBlock[]) {
    const defaultError = DEFAULT_ERROR;

    const baseConditions: TCondition[] = [];
    const orConditions: TCondition[] = [];
    const compareConditions: TCompareCondition[] = [];
    const validationConditions: TValidationCondition[] = [];
    const notZeroConditions: TNonZeroCondition[] = [];
    const maskConditions: TMaskCondition[] = [];

    answers.forEach(({ ID, condition, loop_index, answer_block_alias }) => {
      if (!condition) return;

      if (condition === "select_button_disabled") {
        this.context.commit("setConditionSelectButtonDisabled");

        return;
      }

      const errorMsg = condition.split(/{|}/)[1]?.trim();

      // notZero
      if (condition.includes("notZero")) {
        notZeroConditions.push({
          target_id: ID,
          value: condition,
          loop_index,
          error_message: errorMsg || defaultError,
        });
        return;
      }

      // mask
      if (condition.includes("mask")) {
        maskConditions.push({
          answer_block_id: ID,
          value: condition,
          loop_index,
          error_message: errorMsg || defaultError,
        });
        return;
      }

      // > or <
      if (condition.includes(">") || condition.includes("<")) {
        const isGreater = condition.includes(">");

        const conditionPart = isGreater
          ? condition.split(/ *{ */)[0].split(">")
          : condition.split(/ *{ */)[0].split("<");

        const alias = conditionPart[0];
        const value = conditionPart[1];
        const errorMsg = condition.split(/ *{ */)[1]?.split(/ *} */)[0];

        compareConditions.push({
          target_id: ID,
          answer_block_alias: alias,
          value,
          is_greater: isGreater,
          loop_index,
          error_message: errorMsg || defaultError,
        });

        return;
      }

      // OR (||)
      if (condition.includes("||")) {
        condition.split("||").forEach((item) => {
          const [alias, value] = item.split("==").map((v) => v?.trim() || "");
          if (alias && value) {
            orConditions.push({
              target_id: ID,
              answer_block_alias: alias,
              value,
              visible: false,
              loop_index,
            });
          }
        });
        return;
      }

      // EQUALITY (==)
      if (condition.includes("==")) {
        const [alias, value] = condition.split("==").map((v) => v?.trim() || "");
        if (alias && value) {
          if (alias === answer_block_alias) {
            validationConditions.push({
              target_id: ID,
              answer_block_alias: alias,
              value,
              loop_index,
              error_message: errorMsg || defaultError,
            });
          } else {
            baseConditions.push({
              target_id: ID,
              answer_block_alias: alias,
              value,
              visible: false,
              loop_index,
            });
          }
        }
        return;
      }

      // Default fallback
      baseConditions.push({
        target_id: ID,
        answer_block_alias: condition,
        value: condition,
        visible: true,
        loop_index,
      });
    });

    this.context.commit("setConditionData", {
      conditions: [...baseConditions, ...orConditions],
      compareConditions,
      validationConditions,
      notZeroConditions,
      maskConditions,
    });

    // Update visibility after setting conditions
    this.context.dispatch("updateAnswersWithConditions");
  }

  @Action
  async updateConditions(answers: TAnswerBlock[]) {
    await this.context.dispatch("setCondition", answers);
    await this.context.dispatch("updateAnswersWithConditions");
  }

  @Action updateAnswersWithConditions() {
    this.answers.forEach((answer) => {
      const { answer_block_id, answer_block_alias, loop_index, value } = answer;

      if (this.hasCondition) {
        this.conditions.map((item) => {
          if (answer_block_alias !== item.answer_block_alias || loop_index !== item.loop_index) {
            return item;
          }

          item.visible =
            (!!answer_block_id && item.value === value) ||
            (answer_block_id === item.target_id && item.value === value);

          return item;
        });
      }
    });
  }

  @Mutation
  setAnswers(answers: TAnswerBlock[]) {
    this.loopsCount = Math.max(...answers.map(({ loop_index }) => loop_index), 0) + 1;

    this.answers = answers.map(
      ({
        ID,
        index,
        loop_index,
        condition,
        type,
        alias_result,
        is_required,
        answer_block_alias,
        soc_group_number_answer_block_alias,
      }) => {
        this.hasCondition = this.hasCondition || Boolean(condition);

        if (type === "switcher") is_required = false;
        if (type === "none-of-the-above") this.hasNoneOfTheAboveAnswerBlock = true;
        if (soc_group_number_answer_block_alias) {
          this.socGroupNumberAnswerBlockAlias = soc_group_number_answer_block_alias;
        }

        return {
          value: condition?.includes("notZero") && !Number(alias_result) ? "" : alias_result.trim(),
          required: is_required,
          answer_block_id: ID,
          answer_block_alias,
          loop_index,
          index,
          condition,
          type,
          soc_group_number_answer_block_alias,
        };
      }
    );

    this.answerBlocks = answers;
  }

  @Mutation
  setQuestionRequired(payload: boolean) {
    this.questionIsRequired = payload;
  }

  @Mutation
  setLoopsLimit(question: any) {
    if (!question) return;

    this.loopsLimitMin = 0;
    this.loopsLimit = question.loop_count || 0;

    if (!question.loop_condition_rule) return;

    const rules = question.loop_condition_rule.split(" and ");

    rules.forEach((rule) => {
      const minMatch = rule.match(/loop_amount\s*>=\s*(\d+)/);
      const maxMatch = rule.match(/loop_amount\s*<=\s*(\d+)/);

      if (minMatch) this.loopsLimitMin = parseInt(minMatch[1], 10);
      if (maxMatch) this.loopsLimit = parseInt(maxMatch[1], 10);
    });
  }

  @Mutation
  setDisplayName(name: string) {
    this.display_name = name;
  }

  @Mutation
  addLoop() {
    if (this.loopsLimit && this.loopsCount >= this.loopsLimit) return;

    const loopIndex = this.loopsCount;

    const baseAnswerBlocks = this.answerBlocks.filter(({ loop_index }) => loop_index === 0);

    const newAnswers = baseAnswerBlocks.map(
      ({ ID, index, is_required, condition, answer_block_alias, type }) => ({
        value: "",
        required: is_required,
        answer_block_id: ID,
        loop_index: loopIndex,
        answer_block_alias,
        index,
        condition,
        type,
      })
    );

    const newAnswerBlocks = baseAnswerBlocks.map((block) => ({
      ...block,
      alias_result: "",
      loop_index: loopIndex,
    }));

    const newConditions = this.conditions.map(({ answer_block_alias, target_id, value }) => ({
      answer_block_alias,
      target_id,
      value,
      visible: false,
      loop_index: loopIndex,
    }));

    this.answers.push(...newAnswers);
    this.answerBlocks.push(...newAnswerBlocks);
    this.conditions.push(...newConditions);

    this.loopsCount++;
    this.canGoNext = !this.questionIsRequired;
  }

  @Mutation
  removeLoop(index: number) {
    if (index < 0 || this.loopsCount <= 1) return;

    // remove items with the given loop index
    const filterByLoopIndex = (collection: any[]) =>
      collection.filter(({ loop_index }) => loop_index !== index);

    this.answers = filterByLoopIndex(this.answers);
    this.answerBlocks = filterByLoopIndex(this.answerBlocks);
    this.conditions = filterByLoopIndex(this.conditions);

    this.loopsCount--;

    // update loop indexes
    [this.answers, this.answerBlocks, this.conditions].forEach((collection) => {
      collection.forEach((item) => {
        if (item.loop_index > index) item.loop_index--;
      });
    });

    this.canGoNext = this.answers.every(({ value, required, condition }) =>
      required ? Boolean(value) || condition : true
    );
  }

  @Mutation
  setCurrentStep(step: TStep) {
    step.question.answer_blocks = step.question.answer_blocks.map((ab) => {
      if (ab.options && typeof ab.options == "string") ab.options = JSON.parse(ab.options);

      return ab;
    });

    this.currentStep = step;
  }

  @Mutation
  clearCurrentStep() {
    this.currentStep = null;
    this.answers = [];
    this.isNoneOfTheAboveOptionSelected = false;
  }

  @Mutation
  setStages(stages: TStage[]): void {
    this.stages = stages;
  }

  @Mutation
  setProgress(progress: number): void {
    this.progress = Math.floor(progress * 100);
  }

  @Mutation
  setCanGoNext(payload: boolean) {
    this.canGoNext = payload;
  }

  @Mutation
  updateAnswer({ answer_block_id, answer_block_alias, loop_index, value, filled }: any) {
    this.answers = this.answers.map((answer) =>
      answer.answer_block_id === answer_block_id && answer.loop_index === loop_index
        ? { ...answer, value }
        : answer
    );

    this.canGoNext = true;

    if (this.regex.test(value) || this.answerBlockEditing) {
      this.canGoNext = false;
      return;
    }

    if (this.loopsLimit && this.loopsCount > this.loopsLimit) {
      this.canGoNext = false;
      return;
    }

    this.answers.forEach((answer) => {
      const condition = this.conditions.find(
        ({ target_id, loop_index }) =>
          answer.answer_block_id === target_id && answer.loop_index === loop_index
      );

      if (!condition) {
        if (answer.type === "none-of-the-above" && answer.value) {
          this.canGoNext = true;
          return;
        }
        if ((answer.required && !answer.value) || answer.filled === false) {
          this.canGoNext = false;
          return;
        }
      }
    });

    if (this.hasCondition) {
      this.conditions.forEach((condition) => {
        if (
          condition.answer_block_alias === answer_block_alias &&
          condition.loop_index === loop_index
        ) {
          condition.visible = condition.value === value || answer_block_id === condition.target_id;
        }
      });

      this.conditions.forEach((condition) => {
        if (condition.visible) {
          const answer = this.answers.find(
            ({ answer_block_id, loop_index }) =>
              answer_block_id === condition.target_id && loop_index === condition.loop_index
          );

          if (answer && answer.required && !answer.value) {
            this.canGoNext = false;
          }
        }
      });
    }

    this.errors = this.errors.filter(
      (err) =>
        err.loop_index !== loop_index ||
        !["compare", "validation", "zero", "mask"].includes(err.type)
    );

    // Compare conditions
    this.compareConditions.forEach((item) => {
      const answerLeft = this.answers.find(
        (a) => a.answer_block_alias === item.answer_block_alias && a.loop_index === loop_index
      )?.value;

      let answerRight = this.answers.find(
        (a) => a.answer_block_alias === item.value && a.loop_index === loop_index
      )?.value;

      let isValid = true;

      if (!answerRight) {
        answerRight = item.value;

        isValid = item.is_greater
          ? Number(answerRight) > Number(answerLeft)
          : Number(answerLeft) < Number(answerRight);
      } else if (answerLeft && answerRight) {
        const dateLeft = answerLeft
          .split("/")
          .reverse()
          .join("-");
        const dateRight = answerRight
          .split("/")
          .reverse()
          .join("-");

        isValid = item.is_greater
          ? Date.parse(dateLeft) > Date.parse(dateRight)
          : Date.parse(dateRight) <= Date.parse(dateLeft);
      }

      if (!isValid) {
        this.errors.push({
          answer_block_id: item.target_id,
          loop_index,
          type: "compare",
          error_message: item.error_message,
        });
        this.canGoNext = false;
      }
    });

    // Validation conditions
    this.validationConditions.forEach((item) => {
      const isValid = this.answers.some(
        (a) => a.value === item.value && a.loop_index === loop_index
      );

      if (!isValid) {
        this.errors.push({
          answer_block_id: item.target_id,
          loop_index,
          type: "validation",
          error_message: item.error_message,
        });
        this.canGoNext = false;
      }
    });

    // Not-zero conditions
    this.notZeroConditions.forEach((item) => {
      const answer = this.answers.find(
        (a) => a.answer_block_id === item.target_id && a.loop_index === item.loop_index
      );

      if (!answer?.value || Number(answer.value) === 0) {
        this.errors.push({
          answer_block_id: item.target_id,
          loop_index,
          type: "zero",
          error_message: item.error_message,
        });
        this.canGoNext = false;
      }
    });

    // Mask conditions
    this.maskConditions.forEach((item) => {
      if (filled === false) {
        this.errors.push({
          answer_block_id: item.answer_block_id,
          loop_index,
          type: "mask",
          error_message: item.error_message,
        });

        this.canGoNext = false;
      }
    });
  }

  @Mutation
  setHasHistory(payload: boolean) {
    this.hasHistory = payload;
  }

  @Mutation
  setAnswerQuery(payload: object) {
    this.answerQuery = payload;
  }

  get getAnswerQuery() {
    return this.answerQuery;
  }

  @Mutation
  filterConditionAnswers() {
    this.answers.forEach((answer) => {
      const { condition } = answer;

      if (!condition.includes("==") || condition.includes("{")) return;

      const conditions = condition.includes("||") ? condition.split("||") : [condition];

      const isConditionMet = conditions.some((cond) => {
        const [conditionAlias, expectedValue] = cond.split("==").map((str) => str.trim());
        return this.answers.some(
          (answerBlock) =>
            answerBlock.answer_block_alias === conditionAlias && answerBlock.value === expectedValue
        );
      });

      if (!isConditionMet) answer.value = "";
    });
  }

  @Action
  async openQuestionnaire(id: number) {
    await this.getCurrentStep(id);

    this.setNeedsUpdate(false);
  }

  @Action
  async getCurrentStep(id: number) {
    this.context.commit("setGlobalErrorMessage", "");

    if (!id) return;

    this.userflowID = id;

    this.context.commit("clearCurrentStep");
    this.context.commit("unsetCondition");

    const answerQuery = await this.context.getters["getAnswerQuery"];

    const { data } = await axios.get<TCurrentStep>(
      API_ROUTES.USERFLOW_CURRENT_STEP(this.userflowID, answerQuery)
    );

    this.context.commit("setCurrentStep", data.step);
    this.context.commit("setDisplayName", data.display_name);

    SMHorizontalQuestionnaire.setTaskId(Number(data.task_id));

    if (data.step.question.answer_blocks) {
      this.context.commit("setAnswers", data.step.question.answer_blocks);
      await this.context.dispatch("updateConditions", data.step.question.answer_blocks);
    }

    this.context.commit("setHasHistory", data.has_history);
    this.context.commit("setQuestionRequired", data.step.question.is_required);
    this.context.commit("setLoopsLimit", data.step.question);

    const visibleAnswers = this.answers.filter((answer) => {
      const condition = this.conditions.find(
        (cond) => cond.target_id === answer.answer_block_id && cond.loop_index === answer.loop_index
      );
      return !condition || condition.visible;
    });
    const allRequiredVisibleAnswersFilled = visibleAnswers.every((a) => !a.required || !!a.value);
    this.context.commit(
      "setCanGoNext",
      allRequiredVisibleAnswersFilled || !this.questionIsRequired
    );
    // this.context.commit("setCanGoNext", true);
    // this.answers.forEach((el) => {
    //   if (el.required && !el.value) this.context.commit("setCanGoNext", false);
    // });

    if (!this.questionIsRequired) this.context.commit("setCanGoNext", true);

    this.context.commit("setProgress", data.progress);
    this.context.commit("setStages", data.stages);
    this.context.commit("open");
  }

  globalErrorMessage = "";

  @Mutation
  setGlobalErrorMessage(msg: string): void {
    this.globalErrorMessage = msg;
  }

  @Action
  async goNextQuestion() {
    this.context.commit("setGlobalErrorMessage", "");

    if (!this.userflowID || !this.answers) return;

    if (this.hasCondition) await this.filterConditionAnswers();

    const answer_blocks = this.answers.map(
      ({ value, answer_block_id, answer_block_alias, loop_index }) => ({
        answer: typeof value === "string" ? value.trim() : value,
        answer_block_id,
        answer_block_alias,
        loop_index,
      })
    );

    if (!answer_blocks.length) return;

    const answerQuery = await this.context.getters["getAnswerQuery"];
    const taskId = SMTasks.taskTemplate?.id ?? 0;
    const company_id = SMTasks.companyId ?? 0;

    try {
      const { data } = await axios.post<TCurrentStep>(
        API_ROUTES.USERFLOW_ANSWER(this.userflowID, answerQuery),
        { answer_blocks, taskId, company_id, company_profile_id: company_id }
      );

      if (data.is_passed) {
        if (this.display_name === "Monthly checklist") SMNps.checkNps("report_change");

        this.context.commit("setHasHistory", false);
        this.context.commit("setNeedsUpdate", true);
        this.context.commit("close");
        SMTasks.openSuccessPopup();
        SMNotifications.fetchNotificationsStatus();
        this.context.commit("setLastUpdatedStep", data.step);
        return;
      }

      this.context.commit("clearCurrentStep");
      this.context.commit("unsetCondition");
      this.context.commit("setCurrentStep", data.step);

      if (data.step.question.answer_blocks) {
        this.context.commit("setAnswers", data.step.question.answer_blocks);
        await this.context.dispatch("updateConditions", data.step.question.answer_blocks);
      }

      this.context.commit("setQuestionRequired", data.step.question.is_required);
      this.context.commit("setLoopsLimit", data.step.question);

      this.context.commit("setProgress", data.progress);
      this.context.commit("setHasHistory", data.has_history);
    } catch (e) {
      const error = e as AxiosError;

      if (error.response) {
        if (error.response.data.message == "wrong loop amount") {
          this.context.commit("setGlobalErrorMessage", "Please enter the dates without gaps");
        }
      } else {
        this.context.commit("setGlobalErrorMessage", "Oops! An error occurred.");
      }
    }

    const visibleAnswers = this.answers.filter((answer) => {
      const condition = this.conditions.find(
        (cond) => cond.target_id === answer.answer_block_id && cond.loop_index === answer.loop_index
      );
      return !condition || condition.visible;
    });
    const allRequiredVisibleAnswersFilled = visibleAnswers.every((a) => !a.required || !!a.value);
    this.context.commit(
      "setCanGoNext",
      allRequiredVisibleAnswersFilled || !this.questionIsRequired
    );
    // this.context.commit("setCanGoNext", true);
    // this.answers.forEach((el) =>
    //   (el.value && el.required) || !el.required ? null : this.context.commit("setCanGoNext", false)
    // );

    if (!this.questionIsRequired) this.context.commit("setCanGoNext", true);
  }

  @Action
  async goPreviousQuestion() {
    this.context.commit("setGlobalErrorMessage", "");

    this.context.commit("clearCurrentStep");
    this.context.commit("unsetCondition");

    if (!this.userflowID || !this.answers) return;

    const answerQuery = await this.context.getters["getAnswerQuery"];
    const { data } = await axios.get<TCurrentStep>(
      API_ROUTES.USERFLOW_GO_BACK(this.userflowID, answerQuery)
    );

    this.context.commit("setCurrentStep", data.step);

    if (data.step.question.answer_blocks) {
      this.context.commit("setAnswers", data.step.question.answer_blocks);
      await this.context.dispatch("updateConditions", data.step.question.answer_blocks);
    }

    this.context.commit("setQuestionRequired", data.step.question.is_required);
    this.context.commit("setLoopsLimit", data.step.question);

    const visibleAnswers = this.answers.filter((answer) => {
      const condition = this.conditions.find(
        (cond) => cond.target_id === answer.answer_block_id && cond.loop_index === answer.loop_index
      );
      return !condition || condition.visible;
    });
    const allRequiredVisibleAnswersFilled = visibleAnswers.every((a) => !a.required || !!a.value);
    this.context.commit(
      "setCanGoNext",
      allRequiredVisibleAnswersFilled || !this.questionIsRequired
    );
    // this.context.commit("setCanGoNext", true);
    // this.answers.forEach((el) => {
    //   if (el.required && !el.value) this.context.commit("setCanGoNext", false);
    // });

    if (!this.questionIsRequired) this.context.commit("setCanGoNext", true);
    this.context.commit("setProgress", data.progress);
    this.context.commit("setHasHistory", data.has_history);
  }
}
